import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged,
  GoogleAuthProvider,
  EmailAuthProvider
} from 'firebase/auth';
import { 
  getDatabase, 
  ref as databaseRef, 
  onValue
} from 'firebase/database';
import { 
  getFirestore, 
  enableIndexedDbPersistence
} from 'firebase/firestore';
import { 
  getStorage
} from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';
import config from './firebaseConfig';

class Firebase {
  constructor(onAuthStateChangedCallback) {
    this.onAuthStateChangedCallback = onAuthStateChangedCallback;
    this.reinitFirebase();
  }

  reinitFirebase = () => {
    // Initialize Firebase
    this.app = initializeApp(config);
    
    // Initialize services
    this.auth = getAuth(this.app);
    this.initialised = false;
    this.redirectLogin = false;
    this.connected = false;

    // Analytics (only in browser)
    if (typeof window !== 'undefined') {
      try {
        this.analytics = getAnalytics(this.app);
      } catch (e) {
        console.log('Analytics not available:', e);
      }
    }

    // Firestore
    this.firestore = getFirestore(this.app);
    this.firestorePersistenceEnabled = true;
    
    enableIndexedDbPersistence(this.firestore)
      .catch((err) => {
        if (err.code === 'failed-precondition') {
          // Multiple tabs open, persistence can only be enabled in one tab at a time
          console.log(err.code);
          this.firestorePersistenceEnabled = false;
        } else if (err.code === 'unimplemented') {
          // The current browser does not support all of the features required
          console.log(err.code);
          this.firestorePersistenceEnabled = false;
        }
      });

    // Realtime Database
    this.database = getDatabase(this.app);
    this.firstConnected = false;
    
    // Monitor connection status
    const connectedRef = databaseRef(this.database, '.info/connected');
    onValue(connectedRef, (snap) => {
      if (snap.val()) {
        this.firstConnected = true;
      }
      this.connected = snap.val();
    });

    // Storage
    this.storage = getStorage(this.app);
    this.emailAuthProvider = EmailAuthProvider;
    this.googleProvider = new GoogleAuthProvider();

    // Auth state observer
    onAuthStateChanged(this.auth, (user) => {
      /* This will be invoked if there is a user logged in or not */
      this.user = user;
      this.initialised = true;
      this.onAuthStateChangedCallback(this.user);

      if (this.redirectLogin) {
        getRedirectResult(this.auth)
          .then((result) => {
            this.signInSuccess(result);
            if (this.redirectLoginCallback) {
              this.redirectLoginCallback();
            }
            this.redirectLogin = false;
            this.redirectLoginCallback = null;
          })
          .catch((error) => {
            alert("Got error: " + JSON.stringify(error));
          });
      }
    });
  }

  createUserSuccess = (result) => {
    this.user = result.user;
  }

  signInSuccess = (result) => {
    if (result && result.user) {
      this.user = result.user;
    }
  }

  signOutSuccess = () => {
    this.user = null;
  }

  doCreateUserWithEmailAndPassword = (name, email, password, callback) => {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        // Update profile
        return userCredential.user.updateProfile({
          displayName: name
        }).then(() => {
          this.createUserSuccess(userCredential);
          if (callback) {
            callback(userCredential.user);
          }
        });
      });
  }

  doSignInWithEmailAndPassword = (email, password) => {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        this.signInSuccess(userCredential);
        return userCredential;
      });
  }

  doSignInWithGoogle = (callback) => {
    var userAgent = window.navigator.userAgent.toLowerCase();
    var safari = /safari/.test(userAgent);
    var ios = /iphone|ipod|ipad/.test(userAgent);

    if ((ios && !safari) || ('standalone' in window.navigator && window.navigator.standalone)) {
      this.redirectLogin = true;
      this.redirectLoginCallback = callback;
      return signInWithRedirect(this.auth, this.googleProvider);
    } else {
      return signInWithPopup(this.auth, this.googleProvider)
        .then((result) => {
          this.signInSuccess(result);
          if (callback) {
            callback();
          }
        });
    }
  }

  doSignInWithGoogleUsingRedirect = () => {
    this.redirectLogin = true;
    return signInWithRedirect(this.auth, this.googleProvider);
  }

  doSignInWithToken = (token) => {
    return signInWithCustomToken(this.auth, token)
      .then((userCredential) => {
        this.signInSuccess(userCredential);
      });
  }

  doSignOut = () => {
    return signOut(this.auth).then(this.signOutSuccess);
  }

  doPasswordReset = (email) => {
    return sendPasswordResetEmail(this.auth, email);
  }

  doPasswordUpdate = (password) => {
    if (this.auth.currentUser) {
      return updatePassword(this.auth.currentUser, password);
    }
    return Promise.reject(new Error('No user signed in'));
  }
}

export default Firebase;